import EntityDisplay from '@src/design-system/molecules/ticlick-table/table-cells/entity-display';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';
import type { TLead } from '@shared/types/lead';
import { ArchiveBox, Repeat } from 'iconsax-react';
import {
  EditableInputCell,
  EditableInputCellEditMode,
  EditableInputCellViewMode,
} from '@src/shared/components/editable-input-cell';
import leadApi from '@src/features/leads/services/lead-api';
import { useTableStates } from '@src/design-system/molecules/ticlick-table/hooks/use-table-states';

export const LeadNameCell = ({ lead }: { lead: TLead }) => {
  const { setTableFilters } = useTableStates();

  const updateFiltration = (lead: TLead) => {
    const { duplicate_email, duplicate_phone } = lead;
    if (duplicate_email) {
      setTableFilters((prev) => ({
        ...prev,
        staticFilters: [
          { field: 'term', value: [lead.email] },
          { field: 'excluded_lead_ids', value: [lead.id] },
        ],
      }));
    }
    if (duplicate_phone) {
      setTableFilters((prev) => ({
        ...prev,
        staticFilters: [
          { field: 'term', value: [lead.phone_number] },
          { field: 'excluded_lead_ids', value: [lead.id] },
        ],
      }));
    }
  };

  const { mutate: updateLead, variables } = leadApi.useUpdate();

  const {
    name,
    id,
    formatted_sharable_uid,
    duplicate_email,
    duplicate_phone,
    is_archived,
  } = lead;
  const sub = `#${formatted_sharable_uid ?? id}`;

  return (
    <EditableInputCell copyToText={name}>
      <EditableInputCellEditMode
        value={name}
        onBlur={(e) => {
          if (e.target.value.trim() === name?.trim()) return;
          updateLead({ id, name: e.target.value });
        }}
      />

      <EditableInputCellViewMode>
        <div className='flex items-center gap-1'>
          {is_archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex shrink-0'>
                  <ArchiveBox
                    size={20}
                    variant='Bold'
                    className='text-gray-600'
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>This is an archived lead</TooltipContent>
            </Tooltip>
          )}
          {(duplicate_email || duplicate_phone) && (
            <Tooltip>
              <TooltipTrigger>
                <Repeat
                  onClick={() => {
                    updateFiltration?.(lead);
                    // TODO: disable default view
                  }}
                  color='var(--color-yellow-500)'
                  size={20}
                />
              </TooltipTrigger>
              <TooltipContent>
                This lead {duplicate_email ? 'email' : 'phone'} is duplicated!
              </TooltipContent>
            </Tooltip>
          )}
          <EntityDisplay
            main={variables?.name ?? name}
            sub={sub}
          />
        </div>
      </EditableInputCellViewMode>
    </EditableInputCell>
  );
};
